import React, { type SVGAttributes } from 'react';
import styled, { css } from 'styled-components';
import { handleSizesMediaQueryStyling } from 'helpers/mediaQueryStyling';
import type { ISplash } from './shared/types';
import { filterSplashesByImage } from './shared/helpers';
import useSegmentation from 'hooks/user/useSegmentation';
import dynamic from 'helpers/dynamic';

// Matas splashes
const MatasProHair = dynamic(() => import('icons/splashes/matas/ProHair.svg?react'));
const MatasDerma = dynamic(() => import('icons/splashes/matas/Derma.svg?react'));
const MatasDanishBeautyAward = dynamic(() => import('icons/splashes/matas/DanishBeautyAward.svg?react'));

// Kicks splashes
const KicksProHair = dynamic(() => import('icons/splashes/kicks/ProHair.svg?react'));
const KicksBeautyPharmacy = dynamic(() => import('icons/splashes/kicks/BeautyPharmacy.svg?react'));
const KicksProSkin = dynamic(() => import('icons/splashes/kicks/ProSkin.svg?react'));

const splashSize = {
    sm: css`
        width: 32px;
    `,
    md: css`
        width: 64px;
    `,
    xl: css`
        width: 90px;
    `
};

const Splash = styled.img<{ $size: ISplash['size'] }>`
    ${({ $size }) =>
        $size && (typeof $size === 'object' ? handleSizesMediaQueryStyling($size, splashSize) : splashSize[$size])}
`;

const IconSplash = (props: { type?: ISplash['type'] } & SVGAttributes<SVGElement>) => {
    const { type, className } = props;
    const { tenant } = useSegmentation();

    switch (type) {
        case 'ProHairCare':
            return tenant === 'Matas' ? <MatasProHair className={className} /> : <KicksProHair className={className} />;
        case 'Derma':
            return tenant === 'Matas' ? <MatasDerma className={className} /> : null;
        case 'BeautyPharmacy':
            return tenant === 'Matas' ? null : <KicksBeautyPharmacy className={className} />;
        case 'Dba':
            return tenant === 'Matas' ? <MatasDanishBeautyAward className={className} /> : null;
        case 'ProSkin':
            return tenant === 'Matas' ? null : <KicksProSkin className={className} />;
        default:
            return null;
    }
};

export default function ImageSplash(
    splash: Pick<ISplash, 'type' | 'imageUrl' | 'size' | 'className'>
): JSX.Element | null {
    const { type, imageUrl, size = 'sm', className } = splash;

    if (filterSplashesByImage([splash]).length && !imageUrl) {
        return <IconSplash type={type} className={className} />;
    }

    switch (type) {
        case 'ProHairCare':
        case 'Derma':
        case 'Dba':
        case 'Taenk':
        case 'ProSkin':
        case 'BeautyPharmacy':
            return <Splash src={imageUrl} alt="" $size={size} className={className} />;
        default:
            console.warn(`${type} is not reconized as a valid ImageSplash type`);
            return null;
    }
}
